<template>
	<DialogControl v-model="open" side :subtitle="t('PromoNegotiatedConditions')" :title="product?.label || ''" icon="handshake" :attach="overlayTarget" divided>
		<div class="promo-facts">
			<div class="promo-facts__row">
				<span>{{ t('PromoMechanism') }}</span>
				<span>{{ offer.mechanismLabel || t('N/A') }}</span>
			</div>
			<div class="promo-facts__row">
				<span>{{ t('ProductPrice') }}</span>
				<span>{{ format.money(negoPrice) }} / {{ t('UC') }}</span>
			</div>
			<div class="promo-facts__row">
				<span>{{ t('PromoNegotiatedValue') }}</span>
				<span>{{ format.money(negoValue) }}</span>
			</div>
		</div>

		<div>
			<div class="text-body-medium font-weight-bold mb-1">{{ t('PromoNegotiatedPresences') }} ({{ conditions.presences.length }})</div>
			<v-table density="compact" class="bg-transparent">
				<thead>
					<tr>
						<th>{{ t('Quantity') }}</th>
						<th>{{ t('Location') }}</th>
						<th>{{ t('LocationHighlight') }}</th>
						<th class="text-end">{{ t('UC') }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(line, index) in conditions.presences" :key="index">
						<td>{{ format.number(line.volume) }} {{ labelOf(snapshot.refs.containers, line.productContainerId) }}</td>
						<td>{{ labelOf(snapshot.refs.locations, line.locationPromoId) }}</td>
						<td>{{ line.locationHighlightId ? labelOf(snapshot.refs.highlights, line.locationHighlightId) : t('No') }}</td>
						<td class="text-end font-weight-bold">{{ format.uc(line.volumeUC) }}</td>
					</tr>
					<tr>
						<td colspan="3" class="text-primary">{{ t('Total') }}</td>
						<td class="text-end text-primary font-weight-bold">{{ format.uc(conditions.totalUC) }}</td>
					</tr>
				</tbody>
			</v-table>
		</div>

		<div>
			<div class="text-body-medium font-weight-bold mb-1">{{ t('PromoNegotiatedVsStated') }}</div>
			<v-table density="compact" class="bg-transparent">
				<thead>
					<tr>
						<th />
						<th class="text-end">{{ t('Negociation') }}</th>
						<th class="text-end">{{ t('Constatation') }}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>{{ t('PromoPresenceCount') }}</td>
						<td class="text-end">{{ conditions.presences.length }}</td>
						<td class="text-end">{{ statedCount || '—' }}</td>
					</tr>
					<tr>
						<td>{{ t('PromoTotalVOL') }}</td>
						<td class="text-end">{{ format.uc(target.targetUC) }}</td>
						<td class="text-end">{{ target.statedUC ? format.uc(target.statedUC) : '—' }}</td>
					</tr>
					<tr>
						<td>{{ t('ProductPrice') }}</td>
						<td class="text-end">{{ format.money(negoPrice) }}</td>
						<td class="text-end">{{ stated?.statedValue ? format.money(stated.statedValue) : '—' }}</td>
					</tr>
				</tbody>
			</v-table>
		</div>
	</DialogControl>
</template>

<script setup>
import { computed, inject } from 'vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { getProduct, negoRowsOf, statedRowsOf } from '@/utils/domain/promo/promoModel';
import { negotiatedConditions, productTarget, statedRowOf } from '@/utils/domain/promo/promoStatement';
import { priceOf, productNegoValue } from '@/utils/domain/promo/promoValues';

const open = defineModel({ type: Boolean, default: false });
const props = defineProps({
	campaign: { type: Object, required: true },
	offer: { type: Object, required: true },
	offerRef: { type: Object, required: true },
	productId: { type: Number, required: true },
});

const { snapshot, format, t } = usePromoStep();
const overlayTarget = inject('overlayTarget', null);

const product = computed(() => getProduct(snapshot.value, props.productId));
const conditions = computed(() => negotiatedConditions(snapshot.value, props.offerRef, props.productId));
const target = computed(() => productTarget(snapshot.value, props.offerRef, props.productId));
const stated = computed(() => statedRowOf(snapshot.value, props.offerRef, props.productId));
const statedCount = computed(() => statedRowsOf(snapshot.value, props.offerRef).filter((row) => row.productId === props.productId && (row.additional || row.present === 1)).length);
const negoValue = computed(() => productNegoValue(negoRowsOf(snapshot.value, props.offerRef), props.offerRef, props.productId));
const negoPrice = computed(() => conditions.value.main?.statedValue || priceOf(props.campaign, props.productId));
const labelOf = (items, id) => items.find((item) => item.id === id)?.label || '—';
</script>
